import React, { useState, useMemo, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './GenericTable.module.scss';
import classes from 'classnames';
import Pagination from './Pagination/Pagination';
import { Link } from 'react-router-dom';
import Icon from './Icon/Icon';

export interface TableColumn<T> {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  render?: (value: any, row: T) => ReactNode;
  filterOptions?: string[];
  filterValue?: string;
  onFilterChange?: (value: string) => void;
}

export interface GenericTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string | number;
  detailsPath?: (row: T) => string;
  pageSize?: number;
  noResultsMessage?: any;
  className?: string;
  disablePagination?: boolean;
  variant?: 'compact' | 'card';
  emptyStateAction?: React.ReactNode;
  hideHeaders?: boolean;
  hasData?: boolean;
  showSortArrows?: boolean;
  style?: React.CSSProperties;
  isClassTable?: boolean;
  startSortBy?: string;
  startSortDirection?: 'asc' | 'desc';
}

function GenericTable<T>({
  columns,
  data,
  onRowClick,
  rowKey,
  detailsPath,
  pageSize = 50,
  noResultsMessage,
  className,
  disablePagination = false,
  variant = 'card',
  emptyStateAction,
  hideHeaders,
  hasData,
  showSortArrows = true,
  style,
  isClassTable,
  startSortBy,
  startSortDirection = 'asc',
}: GenericTableProps<T>) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});

  const isFiltered = search.length > 0 || Object.values(filters).some(v => v !== 'All');

  useEffect(() => {
    if (startSortBy && startSortDirection) {
      setSortBy(startSortBy);
      setSortDirection(startSortDirection);
    }
  }, [startSortBy, startSortDirection]);

  useEffect(() => {
    setPage(1);
  }, [variant, disablePagination]);

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable) return;

    if (sortBy === column.key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column.key);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const renderSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable || !showSortArrows) return null;

    return (
      <span className={styles.sortIcon}>
        <Icon
          icon='arrowDownActive'
          className={classes(styles.sortArrowActive, {
            [styles.sortArrowActiveOn]: sortBy === column.key && sortDirection === 'asc',
          })}
        />
        <Icon
          icon='arrowUpActive'
          className={classes(styles.sortArrowActive, {
            [styles.sortArrowActiveOn]: sortBy === column.key && sortDirection === 'desc',
          })}
        />
      </span>
    );
  };

  const sortedData = useMemo(() => {
    if (!sortBy || !sortDirection) return data;

    return [...data].sort((a, b) => {
      let aValue = (a as any)[sortBy];
      let bValue = (b as any)[sortBy];

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      let result = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      if (sortDirection === 'desc') result = -1 * result;

      return result;
    });
  }, [data, sortBy, sortDirection]);

  const paginatedData = useMemo(() => {
    if (disablePagination || variant === 'compact') return sortedData;
    const startIndex = (page - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, page, pageSize, disablePagination, variant]);

  const gridTemplateColumns = columns.map(col => col.width || 'auto').join(' ');

  return (
    <div className={`${styles.tableContainer} ${className || ''}`}>
      <div
        className={classes(styles.tableWrapper, {
          [styles.compact]: variant === 'compact',
        })}
        style={
          {
            gridTemplateColumns,
            '--numberOfColumns': columns.length,
          } as any
        }
      >
        {paginatedData.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateTitle}>
              {noResultsMessage ?? t('admin:studentMetricsDashboard.emptyState.filter.title')}
            </div>
            {!hasData && (
              <div className={styles.emptyStateDesc}>
                {t('admin:studentMetricsDashboard.emptyState.filter.description')}
              </div>
            )}
            {!hasData && emptyStateAction && (
              <div style={{ marginTop: 16, textDecoration: 'none' }}>{emptyStateAction}</div>
            )}
          </div>
        ) : (
          <>
            {!hideHeaders && (
              <div className={styles.tableHeader}>
                {columns.map(column => (
                  <div
                    key={column.key}
                    className={`${styles.headerCell} ${column.sortable ? styles.sortable : ''}`}
                    onClick={() => handleSort(column)}
                  >
                    <div className={styles.headerContent}>
                      <span className={styles.headerLabel}>{column.label}</span>
                      {renderSortIcon(column)}
                    </div>
                    {column.filterOptions && column.onFilterChange && (
                      <select
                        className={styles.headerSelect}
                        value={column.filterValue || ''}
                        onChange={e => {
                          e.stopPropagation();
                          column.onFilterChange?.(e.target.value);
                          setPage(1);
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        {column.filterOptions.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div
              className={styles.rows}
              style={{
                ...(variant === 'compact' ? style : {}),
              }}
            >
              {paginatedData.map(row => {
                const RowComponent = detailsPath ? Link : 'div';
                const rowProps = detailsPath
                  ? {
                      to: detailsPath(row),
                      className: styles.tableRow,
                      style: {
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: 'inherit',
                      },
                    }
                  : {
                      className: styles.tableRow,
                      onClick: onRowClick ? () => onRowClick(row) : undefined,
                    };

                return (
                  <RowComponent key={rowKey(row)} {...(rowProps as any)}>
                    {columns.map(column => (
                      <div key={column.key} className={styles.cell}>
                        {column.render
                          ? column.render((row as any)[column.key], row)
                          : (row as any)[column.key]}
                      </div>
                    ))}
                  </RowComponent>
                );
              })}
            </div>
          </>
        )}
      </div>
      {variant !== 'compact' &&
        !disablePagination &&
        sortedData.length > pageSize &&
        paginatedData.length > 0 && (
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(sortedData.length / pageSize)}
            onPageChange={setPage}
          />
        )}
    </div>
  );
}

export default GenericTable;
